"use client";

import { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import Modal from "@/components/portal/Modal";
import Button from "@/components/ui/Button";
import toast from "react-hot-toast";
import { Copy, PartyPopper } from "lucide-react";
import type { RegisterDiscountInfo } from "@/types/auth";
interface DiscountCodeModalProps {
  open: boolean;
  discount: RegisterDiscountInfo | undefined;
  onClose: () => void;
}

// Returns the Jalali-formatted expiry date, or null if `raw` isn't usable
// for any reason — missing, empty, wrong type, or not a parseable date.
// Callers must treat null as "don't render this line at all": we never show
// a raw/unconverted date string, and this must never throw regardless of
// what the backend actually sends (the discount code itself still renders
// fine either way).
function formatExpiryDate(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    const date = new Date(trimmed);
    if (Number.isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat("fa-IR", {
      year: "numeric",
      month: "long",
      day: "numeric"
    }).format(date);
  } catch {
    // Intl/DateTimeFormat can throw in exotic environments (unsupported
    // locale data, etc.) — never let a date-formatting hiccup break the
    // discount modal.
    return null;
  }
}
export default function DiscountCodeModal({
  open,
  discount,
  onClose
}: DiscountCodeModalProps) {
  const [copied, setCopied] = useState(false);
  const firedRef = useRef(false);
  const expiryLabel = discount ? formatExpiryDate(discount.expires_at) : null;
  useEffect(() => {
    if (!open) {
      firedRef.current = false;
      return;
    }
    if (firedRef.current) return;
    firedRef.current = true;
    const duration = 2200;
    const end = Date.now() + duration;
    (function frame() {
      confetti({
        particleCount: 4,
        angle: 60,
        spread: 70,
        origin: {
          x: 0
        },
        colors: ["#ec4899", "#f472b6", "#facc15", "#22d3ee"]
      });
      confetti({
        particleCount: 4,
        angle: 120,
        spread: 70,
        origin: {
          x: 1
        },
        colors: ["#ec4899", "#f472b6", "#facc15", "#22d3ee"]
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  }, [open]);
  if (!discount) return null;
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(discount.code);
      setCopied(true);
      toast.success("کد تخفیف کپی شد");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("کپی نشد، خودتان کد را کپی کنید");
    }
  };
  return <Modal open={open} onClose={onClose} disableBackdropClose>
      <div className="flex w-full max-w-sm flex-col items-center gap-4 p-8 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-primary">
          <PartyPopper className="h-7 w-7" />
        </span>

        <h2 className="text-xl font-extrabold text-white">خوش اومدی به ایده‌نما 🎉</h2>
        <p className="text-sm text-gray-300">
          {discount.discount_value ? `${discount.discount_value}% تخفیف مخصوص تو، همین حالا ازش استفاده کن` : "این کد تخفیف مخصوص توئه، همین حالا ازش استفاده کن"}
        </p>

        <button type="button" onClick={handleCopy} className="flex w-full items-center justify-between gap-3 rounded-lg border border-dashed border-primary/60 bg-white/5 px-4 py-3 text-left transition hover:bg-white/10">
          <span className="font-mono text-lg font-bold tracking-widest text-primary">{discount.code}</span>
          <Copy className="h-5 w-5 shrink-0 text-gray-300" />
        </button>

        {expiryLabel && <p className="text-xs text-gray-500">اعتبار تا {expiryLabel}</p>}

        <Button onClick={handleCopy} className="w-full">
          {copied ? "کپی شد ✓" : "کپی کد تخفیف"}
        </Button>
      </div>
    </Modal>;
}